/**
 * platform.ts — Adaptador de plataforma (Web / Android / iOS / Desktop)
 *
 * QUÉ HACE:
 *  - Detecta en qué plataforma corre la app (web, android, ios, desktop)
 *  - Expone helpers para acceder a plugins de Capacitor de forma segura
 *  - Detecta entorno Tauri para app de escritorio (Windows/macOS/Linux)
 *  - Evita que el código nativo crashee en el navegador (donde Capacitor no existe)
 *
 * QUÉ EXPORTA:
 *  - platform: 'web' | 'android' | 'ios' | 'desktop'
 *  - isNative: boolean (android/ios via Capacitor)
 *  - isDesktop: boolean (Tauri desktop app)
 *  - isAndroid / isIOS: boolean
 *  - camera, haptics, storage, statusBar, network: adaptadores por plataforma
 *
 * QUÉ NO DEBE HACER:
 *  - No importar componentes React
 *  - No contener lógica de negocio
 *  - No hacer llamadas API
 *
 * REGLA CRÍTICA:
 *  Todo acceso a Capacitor.Plugins o Tauri APIs debe pasar por este módulo
 *  para que el proyecto web funcione sin cambios en cualquier entorno.
 */

// ── Detección de plataforma ──────────────────────────────────────────────────

/** "ios" | "android" | "web" | "desktop" */
export type Platform = "ios" | "android" | "web" | "desktop";

function detectPlatform(): Platform {
  // Tauri v1/v2 inyecta __TAURI_INTERNALS__ o __TAURI__ en window
  // También revisamos User Agent por seguridad en algunas compilaciones
  const ua = navigator.userAgent.toLowerCase();
  const isTauri =
    typeof window !== "undefined" &&
    ("__TAURI_INTERNALS__" in window ||
      "__TAURI__" in window ||
      ua.includes("tauri"));

  if (isTauri) return "desktop";

  // Capacitor inyecta el objeto Capacitor en el window
  const win = window as Window & {
    Capacitor?: { getPlatform?: () => string };
  };
  const cap = win.Capacitor;

  if (!cap) {
    // Si estamos en un entorno nativo pero Capacitor aún no se inyectó,
    // detectamos por User Agent para evitar que falle en el arranque de la APK
    if (ua.includes("capacitor") || ua.includes("android; altenot"))
      return "android";
    return "web";
  }

  const p = cap.getPlatform?.();
  if (p === "ios") return "ios";
  if (p === "android") return "android";
  return "web";
}

export const platform: Platform = detectPlatform();
export const isNative: boolean = platform === "android" || platform === "ios";
export const isDesktop: boolean = platform === "desktop";
export const isAndroid: boolean = platform === "android";
export const isIOS: boolean = platform === "ios";

// ── Cámara ────────────────────────────────────────────────────────────────────

/**
 * Toma una foto.
 * - En nativo: usa @capacitor/camera (acceso delegado al sistema)
 * - En web: devuelve null (la UI usa <EvidenceCamera /> con getUserMedia)
 */
export const camera = {
  async takePhoto(): Promise<string | null> {
    if (!isNative) return null;
    try {
      const { Camera } = await import("@capacitor/camera");
      const { CameraResultType, CameraSource } =
        await import("@capacitor/camera");
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        quality: 85,
        allowEditing: false,
        correctOrientation: true,
      });
      return photo.dataUrl ?? null;
    } catch {
      return null;
    }
  },
};

// ── Haptics (vibración) ───────────────────────────────────────────────────────

export const haptics = {
  async light(): Promise<void> {
    if (!isNative) return;
    try {
      const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch {
      /* no-op */
    }
  },

  async medium(): Promise<void> {
    if (!isNative) return;
    try {
      const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch {
      /* no-op */
    }
  },

  async success(): Promise<void> {
    if (!isNative) return;
    try {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({ type: NotificationType.Success });
    } catch {
      /* no-op */
    }
  },

  async error(): Promise<void> {
    if (!isNative) return;
    try {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({ type: NotificationType.Error });
    } catch {
      /* no-op */
    }
  },
};

// ── Storage (key-value persistente) ──────────────────────────────────────────

/**
 * En nativo usa @capacitor/preferences (más robusto que localStorage en iOS).
 * En web usa localStorage con el mismo API para compatibilidad total.
 */
export const storage = {
  async get(key: string): Promise<string | null> {
    if (!isNative) return localStorage.getItem(key);
    try {
      const { Preferences } = await import("@capacitor/preferences");
      const { value } = await Preferences.get({ key });
      return value;
    } catch {
      return localStorage.getItem(key);
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (!isNative) {
      localStorage.setItem(key, value);
      return;
    }
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.set({ key, value });
    } catch {
      localStorage.setItem(key, value);
    }
  },

  async remove(key: string): Promise<void> {
    if (!isNative) {
      localStorage.removeItem(key);
      return;
    }
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.remove({ key });
    } catch {
      localStorage.removeItem(key);
    }
  },
};

// ── StatusBar ─────────────────────────────────────────────────────────────────

export const statusBar = {
  async setDark(): Promise<void> {
    if (!isNative) return;
    try {
      const { StatusBar, Style } = await import("@capacitor/status-bar");
      await StatusBar.setStyle({ style: Style.Dark });
    } catch {
      /* no-op */
    }
  },

  async setLight(): Promise<void> {
    if (!isNative) return;
    try {
      const { StatusBar, Style } = await import("@capacitor/status-bar");
      await StatusBar.setStyle({ style: Style.Light });
    } catch {
      /* no-op */
    }
  },

  async hide(): Promise<void> {
    if (!isNative) return;
    try {
      const { StatusBar } = await import("@capacitor/status-bar");
      await StatusBar.hide();
    } catch {
      /* no-op */
    }
  },

  async show(): Promise<void> {
    if (!isNative) return;
    try {
      const { StatusBar } = await import("@capacitor/status-bar");
      await StatusBar.show();
    } catch {
      /* no-op */
    }
  },
};

// ── Network ───────────────────────────────────────────────────────────────────

export const network = {
  async isConnected(): Promise<boolean> {
    if (!isNative) return navigator.onLine;
    try {
      const { Network } = await import("@capacitor/network");
      const status = await Network.getStatus();
      return status.connected;
    } catch {
      return navigator.onLine;
    }
  },
};

// ── Notificaciones locales ────────────────────────────────────────────────────

/**
 * Notificaciones locales del sistema operativo — las dispara la propia app
 * cuando detecta un evento (venta, cierre de caja, gasto, stock bajo, etc.).
 * NO es push remoto: no requiere backend ni Firebase Cloud Messaging, es 100%
 * local y compatible con la estrategia de mocks/demo del proyecto.
 *
 * - Nativo (Android/iOS): @capacitor/local-notifications
 * - Escritorio (Tauri): @tauri-apps/plugin-notification
 * - Web: Notification API del navegador
 */
export const notifications = {
  /** true si la plataforma actual puede mostrar notificaciones del sistema */
  isSupported(): boolean {
    if (isNative || isDesktop) return true;
    return typeof window !== "undefined" && "Notification" in window;
  },

  /** Solicita permiso al usuario (idempotente si ya fue concedido/denegado). */
  async requestPermission(): Promise<boolean> {
    if (isNative) {
      try {
        const { LocalNotifications } =
          await import("@capacitor/local-notifications");
        const result = await LocalNotifications.requestPermissions();
        return result.display === "granted";
      } catch {
        return false;
      }
    }
    if (isDesktop) {
      try {
        const { isPermissionGranted, requestPermission } =
          await import("@tauri-apps/plugin-notification");
        if (await isPermissionGranted()) return true;
        const permission = await requestPermission();
        return permission === "granted";
      } catch {
        return false;
      }
    }
    if (typeof window === "undefined" || !("Notification" in window)) {
      return false;
    }
    if (Notification.permission === "granted") return true;
    if (Notification.permission === "denied") return false;
    try {
      const result = await Notification.requestPermission();
      return result === "granted";
    } catch {
      return false;
    }
  },

  /** Dispara una notificación real en la plataforma actual (best-effort, nunca lanza). */
  async send(
    title: string,
    body: string,
    options?: {
      /** Android: canal (categoría) al que pertenece la notificación. */
      channelId?: string;
      /** Android: ícono pequeño universal (res/drawable, sin extensión). */
      smallIcon?: string;
      /** Android: color de acento de la categoría. */
      iconColor?: string;
      /** Android: ícono grande a color en la esquina (res/drawable, sin extensión). */
      largeIcon?: string;
      /** ID numérico fijo (Android) — si se omite, se genera uno pseudo-único. */
      id?: number;
      /** Datos para deep-link al tocar la notificación (ej. logId de Auditoría). */
      data?: Record<string, string>;
      /** ID de tipo de acción registrado (botones rápidos, ej. "Marcar como leído"). */
      actionTypeId?: string;
      /** Texto del botón de acción rápida (solo lo usa el plugin nativo de Android). */
      actionLabel?: string;
      /** Prioridad lógica del evento — sube la insistencia (heads-up) en Android. */
      priority?: "default" | "high" | "max";
    },
  ): Promise<void> {
    if (isAndroid) {
      // Android NO usa @capacitor/local-notifications para estas notificaciones:
      // ese plugin no soporta `BigPictureStyle` (confirmado leyendo su código
      // nativo, `LocalNotificationManager.java`) y su `largeIcon` se renderiza
      // como avatar A LA IZQUIERDA en OneUI/Samsung (confirmado en dispositivo
      // real), no "grande al final del cuerpo" como pide el diseño. Por eso se
      // usa el plugin nativo propio `AltenotShellPlugin` (mismo que la barra de
      // progreso de Meta del Día), que arma un `BigPictureStyle` real:
      // smallIcon = logo universal (izquierda, junto al nombre de la app),
      // largeIcon = ícono del módulo (vista previa arriba a la derecha mientras
      // está colapsada) y bigPicture = el mismo ícono del módulo, grande, al
      // final del cuerpo cuando se expande.
      try {
        const { registerPlugin } = await import("@capacitor/core");
        const AltenotShell = registerPlugin<{
          showCategoryNotification(opts: {
            id: number;
            channelId?: string;
            title: string;
            body: string;
            moduleIcon?: string;
            iconColor?: string;
            highPriority: boolean;
            deepLinkUri?: string;
            markReadDeepLinkUri?: string;
            actionLabel?: string;
          }): Promise<void>;
        }>("AltenotShell");
        const logId = options?.data?.logId;
        const deepLinkUri = logId
          ? `altenot://auditoria?openLogId=${encodeURIComponent(logId)}`
          : undefined;
        await AltenotShell.showCategoryNotification({
          id: options?.id ?? Math.floor(Date.now() % 2147483647),
          channelId: options?.channelId,
          title,
          body,
          moduleIcon: options?.largeIcon,
          iconColor: options?.iconColor,
          highPriority:
            options?.priority === "high" || options?.priority === "max",
          deepLinkUri,
          markReadDeepLinkUri: deepLinkUri
            ? `${deepLinkUri}&markRead=1`
            : undefined,
          actionLabel: deepLinkUri ? options?.actionLabel : undefined,
        });
      } catch {
        /* no-op: notificar es best-effort */
      }
      return;
    }
    if (isNative) {
      // iOS: sigue usando @capacitor/local-notifications (sin el problema de
      // OneUI/Samsung que motivó el camino nativo separado en Android).
      try {
        const { LocalNotifications } =
          await import("@capacitor/local-notifications");
        await LocalNotifications.schedule({
          notifications: [
            {
              id: options?.id ?? Math.floor(Date.now() % 2147483647),
              title,
              body,
              channelId: options?.channelId,
              smallIcon: options?.smallIcon,
              iconColor: options?.iconColor,
              largeIcon: options?.largeIcon,
              extra: options?.data,
              actionTypeId: options?.actionTypeId,
              autoCancel: true,
            },
          ],
        });
      } catch {
        /* no-op: notificar es best-effort */
      }
      return;
    }
    if (isDesktop) {
      try {
        const { isPermissionGranted, sendNotification } =
          await import("@tauri-apps/plugin-notification");
        // NOTA: el plugin de Tauri (src/desktop.rs) SOLO lee title/body/icon/sound
        // en escritorio — channelId, smallIcon, largeIcon, iconColor y attachments
        // se ignoran silenciosamente fuera de Android/iOS (confirmado en el código
        // fuente del plugin). Por eso NO se reenvían aquí: agregarlos sería código
        // muerto. Si no se pasa `icon`, el plugin usa `auto_icon()` (ícono de la
        // app real, vía AppUserModelID en Windows / ícono del escritorio en Linux),
        // que es el comportamiento correcto para producción.
        // LIMITACIÓN CONOCIDA: en `npm run tauri dev` (Windows), el SO siempre
        // muestra el ícono/nombre de PowerShell en vez del de la app — es una
        // limitación documentada de Tauri, solo se resuelve probando el build
        // empaquetado/instalado (no el modo dev).
        if (await isPermissionGranted()) {
          sendNotification({ title, body });
        }
      } catch {
        /* no-op */
      }
      return;
    }
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    try {
      // `icon`/`badge` deben ser URLs de assets públicos (no rutas de recursos
      // Android): usamos el logo universal de la app, servido desde /public.
      new Notification(title, {
        body,
        icon: "/icons/icon-256.png",
        badge: "/icons/icon-128.png",
      });
    } catch {
      /* no-op */
    }
  },

  /**
   * Registra un tipo de acción rápida (botones dentro de la notificación, ej.
   * "Marcar como leído"). Debe llamarse una sola vez (idempotente) antes de
   * usar ese `actionTypeId` en `send()`. No-op en Web/Escritorio.
   */
  async registerActionType(
    id: string,
    actions: Array<{ id: string; title: string }>,
  ): Promise<void> {
    if (!isNative) return;
    try {
      const { LocalNotifications } =
        await import("@capacitor/local-notifications");
      await LocalNotifications.registerActionTypes({
        types: [{ id, actions }],
      });
    } catch {
      /* no-op */
    }
  },

  /**
   * Escucha el toque del usuario sobre una notificación local o sobre una de
   * sus acciones rápidas (Android/iOS) y entrega el `data` (deep-link) junto
   * con el `actionId` ("tap" = toque normal; otro valor = botón de acción).
   * Devuelve una función para dejar de escuchar (usar en el cleanup de un
   * `useEffect`). No-op en Web/Escritorio.
   */
  onNotificationClick(
    callback: (data: Record<string, string>, actionId: string) => void,
  ): () => void {
    if (!isNative) return () => undefined;
    let handle: { remove: () => void } | null = null;
    let cancelled = false;
    import("@capacitor/local-notifications")
      .then(({ LocalNotifications }) =>
        LocalNotifications.addListener(
          "localNotificationActionPerformed",
          (action) => {
            const extra = (action.notification.extra ?? {}) as Record<
              string,
              string
            >;
            callback(extra, action.actionId);
          },
        ),
      )
      .then((h) => {
        if (cancelled) h.remove();
        else handle = h;
      })
      .catch(() => {
        /* no-op */
      });
    return () => {
      cancelled = true;
      handle?.remove();
    };
  },

  /**
   * Crea (o actualiza) un canal de notificaciones Android. Necesario para que
   * cada categoría (Ventas/Inventario/Personal/Sistema) tenga su propio grupo
   * visible y configurable por el usuario en Ajustes del sistema, en vez de
   * caer todas en el canal "default" (todas mezcladas en una sola bandeja).
   * No-op en Web/Escritorio (solo aplica a Android 8+).
   */
  async createChannel(channel: {
    id: string;
    name: string;
    description?: string;
    color?: string;
  }): Promise<void> {
    if (!isNative) return;
    try {
      const { LocalNotifications } =
        await import("@capacitor/local-notifications");
      await LocalNotifications.createChannel({
        id: channel.id,
        name: channel.name,
        description: channel.description,
        importance: 4, // IMPORTANCE_HIGH
        visibility: 1, // VISIBILITY_PUBLIC
        lightColor: channel.color,
      });
    } catch {
      /* no-op */
    }
  },

  /**
   * Notificación con barra de progreso nativa (estilo DiDi/Rappi "pedido en
   * curso"), usada para la Meta del Día en Ventas. SOLO Android: iOS no
   * expone progress bars en notificaciones y Web no tiene equivalente — en
   * esas plataformas la llamada es un no-op silencioso.
   * Requiere el plugin nativo `AltenotShellPlugin` (ver
   * android/app/src/main/java/com/altenot/app/AltenotShellPlugin.java).
   */
  async showGoalProgress(params: {
    id: number;
    title: string;
    body: string;
    /** 0-100 */
    progress: number;
    channelId?: string;
    /** false cuando la meta ya se alcanzó (deja de ser "en curso") */
    ongoing?: boolean;
    /** true la primera vez que se alcanza la meta en el día: diseño festivo. */
    celebrate?: boolean;
    /** URI de deep-link (ej. "altenot://dashboard") para navegar al tocarla. */
    deepLinkUri?: string;
  }): Promise<void> {
    if (!isAndroid) return;
    try {
      const { registerPlugin } = await import("@capacitor/core");
      const AltenotShell = registerPlugin<{
        showGoalProgressNotification(opts: {
          id: number;
          title: string;
          body: string;
          progress: number;
          channelId?: string;
          ongoing?: boolean;
          celebrate?: boolean;
          deepLinkUri?: string;
        }): Promise<void>;
      }>("AltenotShell");
      await AltenotShell.showGoalProgressNotification(params);
    } catch {
      /* no-op */
    }
  },

  /** Retira la notificación de progreso (ej. al iniciar un nuevo día). */
  async cancelGoalProgress(id: number): Promise<void> {
    if (!isAndroid) return;
    try {
      const { registerPlugin } = await import("@capacitor/core");
      const AltenotShell = registerPlugin<{
        cancelGoalProgressNotification(opts: { id: number }): Promise<void>;
      }>("AltenotShell");
      await AltenotShell.cancelGoalProgressNotification({ id });
    } catch {
      /* no-op */
    }
  },
};
